import React from 'react'
import { showMessage } from 'shared/utils/showMessage'

// Те же ограничения, что проверяет сервер: так отказ виден до отправки
const CHAT_IMAGES_LIMIT = 10
const CHAT_IMAGE_MAX_MB = 10
const CHAT_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']
const CHAT_VIDEOS_LIMIT = 1
const CHAT_VIDEO_MAX_MB = 300
const CHAT_VIDEO_MAX_SECONDS = 120
const CHAT_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm']
export const CHAT_MEDIA_ACCEPT = [...CHAT_IMAGE_TYPES, ...CHAT_VIDEO_TYPES].join(',')

export interface IChatAttachment {
  id: string
  file: File
  url: string
  kind: 'image' | 'video'
}

let attachmentId = 0

// Длительность — из метаданных; если браузер не разобрал ролик (HEVC в Chrome), её проверит сервер
const readVideoDuration = (url: string) => new Promise<number>((resolve) => {
  const video = document.createElement('video')
  const timer = setTimeout(() => resolve(NaN), 5000)
  const done = (duration: number) => {
    clearTimeout(timer)
    video.removeAttribute('src')
    resolve(duration)
  }
  video.preload = 'metadata'
  video.onloadedmetadata = () => done(video.duration)
  video.onerror = () => done(NaN)
  video.src = url
})

export const useChatAttachments = (resetKey: string | null) => {
  const [attachments, setAttachments] = React.useState<IChatAttachment[]>([])
  const attachmentsRef = React.useRef(attachments)
  attachmentsRef.current = attachments
  const pendingVideos = React.useRef(0)

  const clear = React.useCallback(() => {
    attachmentsRef.current.forEach((item) => URL.revokeObjectURL(item.url))
    setAttachments([])
  }, [])

  React.useEffect(() => clear, [clear])
  React.useEffect(() => {
    clear()
  }, [clear, resetKey])

  const countOf = (kind: IChatAttachment['kind']) =>
    attachmentsRef.current.filter((item) => item.kind === kind).length

  const addVideo = async (file: File) => {
    if (file.size > CHAT_VIDEO_MAX_MB * 1024 * 1024) {
      showMessage('error', 'Видео не приложено', `«${file.name}» больше ${CHAT_VIDEO_MAX_MB} МБ`)
      return
    }
    if (countOf('video') + pendingVideos.current >= CHAT_VIDEOS_LIMIT) {
      showMessage('error', 'Видео не приложено', 'Можно приложить одно видео')
      return
    }

    pendingVideos.current += 1
    const url = URL.createObjectURL(file)
    const duration = await readVideoDuration(url)
    pendingVideos.current -= 1

    if (duration > CHAT_VIDEO_MAX_SECONDS) {
      URL.revokeObjectURL(url)
      showMessage('error', 'Видео не приложено', `«${file.name}» длиннее ${CHAT_VIDEO_MAX_SECONDS / 60} минут`)
      return
    }
    attachmentId += 1
    const item: IChatAttachment = { id: String(attachmentId), file, url, kind: 'video' }
    setAttachments((prev) => [item, ...prev])
  }

  const add = (files: File[]) => {
    const accepted: IChatAttachment[] = []
    for (const file of files) {
      if (CHAT_VIDEO_TYPES.includes(file.type)) {
        addVideo(file)
      } else if (!CHAT_IMAGE_TYPES.includes(file.type)) {
        showMessage('error', 'Файл не приложен', `«${file.name}»: подходят фото JPG, PNG, WebP или видео MP4, MOV, WebM`)
      } else if (file.size > CHAT_IMAGE_MAX_MB * 1024 * 1024) {
        showMessage('error', 'Фото не приложено', `«${file.name}» больше ${CHAT_IMAGE_MAX_MB} МБ`)
      } else if (countOf('image') + accepted.length >= CHAT_IMAGES_LIMIT) {
        showMessage('error', 'Фото не приложено', `Можно приложить не больше ${CHAT_IMAGES_LIMIT} фото`)
        break
      } else {
        attachmentId += 1
        accepted.push({ id: String(attachmentId), file, url: URL.createObjectURL(file), kind: 'image' })
      }
    }
    if (accepted.length) setAttachments((prev) => [...prev, ...accepted])
  }

  const remove = (id: string) => {
    setAttachments((prev) => prev.filter((item) => {
      if (item.id === id) URL.revokeObjectURL(item.url)
      return item.id !== id
    }))
  }

  // Скриншот из буфера прикладывается как фото, текст вставляется как обычно
  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const files = Array.from(event.clipboardData.files)
    if (!files.length) return

    event.preventDefault()
    add(files)
  }

  const filesOf = (kind: IChatAttachment['kind']) =>
    attachments.filter((item) => item.kind === kind).map((item) => item.file)

  return {
    attachments,
    images: filesOf('image'),
    videos: filesOf('video'),
    add,
    remove,
    clear,
    handlePaste
  }
}
